/**
 * FileUploadTab Component
 * Tab content for uploading file-based documents
 * Handles file selection, display, and upload progress
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as InsertDriveFileIcon
} from '@mui/icons-material';
import { FileUploadTabProps } from '../types';

export const FileUploadTab: React.FC<FileUploadTabProps> = ({
  selectedFile,
  onFileSelect,
  onBrowse,
  uploading,
  progress,
  fileInputRef,
  fileError,
  maxFileSizeMB = 10
}) => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Hidden file input */}
      <input
        type="file"
        hidden
        accept=".pdf,.docx,.pptx,.txt"
        ref={fileInputRef}
        onChange={onFileSelect}
      />

      {/* File size validation error */}
      {fileError && (
        <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
          {fileError}
        </Alert>
      )}

      {/* File selection UI */}
      {!selectedFile ? (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('documents.upload.instructions')}
          </Typography>
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={onBrowse}
          >
            {t('documents.upload.browse')}
          </Button>
          <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
            {t('documents.upload.supportedFormats')}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            Maximum file size: {maxFileSizeMB}MB
          </Typography>
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 2 }}>
          {/* Selected file card */}
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <InsertDriveFileIcon sx={{ mr: 1 }} />
                <Typography variant="body1" sx={{ flexGrow: 1 }}>
                  {selectedFile.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {(selectedFile.size / 1024).toFixed(1)} KB
                </Typography>
              </Box>
            </CardContent>
          </Card>
          <Button
            variant="outlined"
            size="small"
            onClick={onBrowse}
            sx={{ mr: 1 }}
          >
            {t('documents.upload.change')}
          </Button>
        </Box>
      )}

      {/* Upload progress */}
      {uploading && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={progress} />
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
            {progress < 100
              ? t('documents.upload.uploading')
              : t('documents.upload.processing')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
